import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Button,
  IconButton,
  Tabs,
  Tab,
  Tooltip,
  Stack,
  alpha,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import {
  Notifications as NotifIcon,
  CheckCircleOutline as ReadIcon,
  DoneAll as MarkAllIcon,
  Circle as UnreadDot,
  AttachMoney as PayrollIcon,
  EventAvailable as LeaveIcon,
  Home as RentIcon,
  SwapHoriz as IntercoIcon,
  Info as DefaultIcon,
  Send as SendIcon,
  Message as MessageIcon,
} from '@mui/icons-material';
import {
  notificationsApi,
  NotificationResponse,
  UserSearchResult,
} from '@/api/endpoints/notifications';
import { useNotificationStore } from '@/stores/notificationStore';
import { buildNotificationLink } from '@/utils/notificationLinks';
import { TYPE } from '@/theme/typography';
import { NOTIFICATION_COLORS, STATUS_COLORS } from '@/theme/colorTokens';
import RichTextEditor from '@/components/common/RichTextEditor';
import BrandedDialogTitle from '@/components/common/BrandedDialogTitle';
import PageHelpIcon from '@/components/PageHelpIcon';
import { formatDate } from '@/utils/format';

const TYPE_CONFIG: Record<string, { icon: React.ReactNode; color: string }> = {
  payroll_processed: {
    icon: <PayrollIcon fontSize="small" />,
    color: NOTIFICATION_COLORS.payroll_processed,
  },
  leave_approved: { icon: <LeaveIcon fontSize="small" />, color: NOTIFICATION_COLORS.leave_approved },
  leave_rejected: { icon: <LeaveIcon fontSize="small" />, color: NOTIFICATION_COLORS.leave_rejected },
  rent_reminder: { icon: <RentIcon fontSize="small" />, color: NOTIFICATION_COLORS.rent_reminder },
  intercompany_confirmation: {
    icon: <IntercoIcon fontSize="small" />,
    color: NOTIFICATION_COLORS.intercompany_confirmation,
  },
  message: { icon: <MessageIcon fontSize="small" />, color: NOTIFICATION_COLORS.message },
};

function getTypeConfig(type: string | null) {
  return (
    TYPE_CONFIG[type || ''] || {
      icon: <DefaultIcon fontSize="small" />,
      color: STATUS_COLORS.default,
    }
  );
}

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return formatDate(dateStr);
}

function formatType(type: string | null): string {
  if (!type) return 'General';
  return type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotificationStore();
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(1);

  // Compose dialog state
  const [composeOpen, setComposeOpen] = useState(false);
  const [recipient, setRecipient] = useState<UserSearchResult | null>(null);
  const [msgTitle, setMsgTitle] = useState('');
  const [msgBody, setMsgBody] = useState('');
  const [userOptions, setUserOptions] = useState<UserSearchResult[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const handleUserSearch = useCallback(
    async (_: unknown, query: string) => {
      if (query.length < 1) {
        setUserOptions([]);
        return;
      }
      setLoadingUsers(true);
      try {
        const res = await notificationsApi.searchUsers(query);
        setUserOptions(res.data);
      } catch {
        setUserOptions([]);
      } finally {
        setLoadingUsers(false);
      }
    },
    [],
  );

  const sendMutation = useMutation({
    mutationFn: (data: {
      recipient_id: string;
      title: string;
      body?: string;
    }) => notificationsApi.send(data),
    onSuccess: () => {
      showSuccess('Notification sent');
      setComposeOpen(false);
      setRecipient(null);
      setMsgTitle('');
      setMsgBody('');
    },
    onError: () => {
      showError('Failed to send notification');
    },
  });

  const handleSend = () => {
    if (!recipient || !msgTitle.trim()) return;
    sendMutation.mutate({
      recipient_id: recipient.id,
      title: msgTitle.trim(),
      body: msgBody.trim() || undefined,
    });
  };

  const handleComposeClose = () => {
    setComposeOpen(false);
    setRecipient(null);
    setMsgTitle('');
    setMsgBody('');
    setUserOptions([]);
  };

  const isReadFilter =
    activeTab === 0 ? undefined : activeTab === 1 ? false : true;

  const { data, isLoading } = useQuery({
    queryKey: ['notifications', isReadFilter, page],
    queryFn: () =>
      notificationsApi
        .list({ is_read: isReadFilter, page, per_page: 25 })
        .then((r) => r.data),
  });

  const markReadMutation = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread'] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread'] });
    },
  });

  const items: NotificationResponse[] = data?.items || [];
  const total = data?.total || 0;
  const hasMore = page * 25 < total;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <NotifIcon color="primary" />
          <Typography variant="h4">Notifications</Typography>
          <PageHelpIcon pageKey="notifications" />
          {total > 0 && (
            <Chip
              label={total}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            size="small"
            startIcon={<SendIcon />}
            onClick={() => setComposeOpen(true)}
          >
            Send Notification
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<MarkAllIcon />}
            onClick={() => markAllReadMutation.mutate()}
            disabled={markAllReadMutation.isPending}
          >
            Mark All Read
          </Button>
        </Stack>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_, v) => {
          setActiveTab(v);
          setPage(1);
        }}
        sx={{ mb: 3 }}
      >
        <Tab label="All" />
        <Tab label="Unread" />
        <Tab label="Read" />
      </Tabs>

      <Card>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {isLoading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                Loading notifications...
              </Typography>
            </Box>
          ) : items.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <NotifIcon
                sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }}
              />
              <Typography color="text.secondary">
                {activeTab === 1
                  ? 'No unread notifications'
                  : 'No notifications yet'}
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {items.map((n, idx) => {
                const config = getTypeConfig(n.type);
                return (
                  <ListItem
                    key={n.id}
                    divider={idx < items.length - 1}
                    sx={{
                      px: 3,
                      py: 1.5,
                      bgcolor: n.is_read
                        ? 'transparent'
                        : (theme) =>
                            alpha(theme.palette.primary.main, 0.04),
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                    onClick={() => {
                      if (!n.is_read) markReadMutation.mutate(n.id);
                      const link = buildNotificationLink(
                        n.type,
                        n.reference_type,
                        n.reference_id,
                      );
                      if (link && link !== '/notifications') navigate(link);
                    }}
                    secondaryAction={
                      !n.is_read ? (
                        <Tooltip title="Mark as read">
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              markReadMutation.mutate(n.id);
                            }}
                          >
                            <ReadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : undefined
                    }
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: alpha(config.color, 0.1),
                          color: config.color,
                        }}
                      >
                        {config.icon}
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center">
                          {!n.is_read && (
                            <UnreadDot
                              sx={{ fontSize: 8, color: 'primary.main' }}
                            />
                          )}
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: n.is_read ? 400 : 600,
                              fontSize: TYPE.md,
                            }}
                          >
                            {n.title}
                          </Typography>
                          <Chip
                            label={formatType(n.type)}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: TYPE.sm, height: 20 }}
                          />
                        </Stack>
                      }
                      secondary={
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ mt: 0.5 }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ maxWidth: '70%' }}
                          >
                            {n.body || ''}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.disabled"
                            sx={{ whiteSpace: 'nowrap' }}
                          >
                            {formatTime(n.created_at)}
                          </Typography>
                        </Stack>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          )}

          {/* Pagination */}
          {total > 25 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 2,
                p: 2,
              }}
            >
              <Button
                size="small"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <Typography
                variant="body2"
                sx={{ alignSelf: 'center', color: 'text.secondary' }}
              >
                Page {page} of {Math.ceil(total / 25)}
              </Typography>
              <Button
                size="small"
                disabled={!hasMore}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Compose Notification Dialog */}
      <Dialog
        open={composeOpen}
        onClose={handleComposeClose}
        maxWidth="sm"
        fullWidth
      >
        <BrandedDialogTitle>Send Notification</BrandedDialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            pt: '16px !important',
          }}
        >
          <Autocomplete
            options={userOptions}
            getOptionLabel={(opt) =>
              `${opt.first_name} ${opt.last_name} (${opt.email})`
            }
            value={recipient}
            onChange={(_, val) => setRecipient(val)}
            onInputChange={handleUserSearch}
            loading={loadingUsers}
            filterOptions={(x) => x}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Recipient"
                placeholder="Search by name or email..."
                required
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingUsers && <CircularProgress size={18} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
          />
          <TextField
            label="Title"
            value={msgTitle}
            onChange={(e) => setMsgTitle(e.target.value)}
            required
            inputProps={{ maxLength: 255 }}
          />
          <RichTextEditor
            label="Message (optional)"
            value={msgBody}
            onChange={setMsgBody}
            placeholder="Write your message..."
            minHeight={100}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleComposeClose}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={handleSend}
            disabled={
              !recipient || !msgTitle.trim() || sendMutation.isPending
            }
          >
            {sendMutation.isPending ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
